import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { ConsentStatusItem } from '@/api/consents/consentsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CONSENT_DECISION } from '@/constants/consents';
import { useWithdrawOwnConsentMutation } from '@/hooks/useClientPortal';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { WithdrawConsentDialog } from '../WithdrawConsentDialog';

interface ConsentsSubsectionProps {
  consents: ConsentStatusItem[];
}

export function ConsentsSubsection({ consents }: ConsentsSubsectionProps) {
  const { t } = useTranslation('clients');
  const withdrawMutation = useWithdrawOwnConsentMutation();
  const [consentToWithdraw, setConsentToWithdraw] = useState<ConsentStatusItem | null>(null);

  function withdraw(consent: ConsentStatusItem) {
    withdrawMutation.mutate(consent.documentId, {
      onSuccess: function done() {
        showSuccessNotification(t('consents.withdrawn'));
        setConsentToWithdraw(null);
      },
      onError: function fail(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('consents.withdrawError'));
      },
    });
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="ShieldCheck" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('portal.consentsStatusTitle')}</h2>
      </div>
      <p className="text-xs text-zoomuted mb-3">{t('portal.pages.forms.consentsHint')}</p>
      <div className="divide-y divide-zooborder">
        {consents.map(function renderConsent(item) {
          const isAgreed = item.decision === CONSENT_DECISION.agreed;
          const isWithdrawn = item.decision === CONSENT_DECISION.withdrawn;
          return (
            <div key={item.documentId} className="flex items-center gap-2 py-2 text-[13px]">
              <span
                role="img"
                aria-label={
                  isAgreed
                    ? t('consents.agreed')
                    : isWithdrawn
                      ? t('consents.withdrawnStatus')
                      : t('consents.declined')
                }
                className={isAgreed ? 'text-zoosuccesstext' : 'text-zooerrortext'}
              >
                <Icon name={isAgreed ? 'Check' : 'X'} />
              </span>
              <span className="text-zooindigo flex-1 min-w-0 truncate">{item.title}</span>
              {isAgreed ? (
                <Button
                  variant="destructiveGhost"
                  size="chip"
                  className="shrink-0 min-h-[44px]"
                  onClick={function askToWithdraw() {
                    setConsentToWithdraw(item);
                  }}
                >
                  {t('consents.withdraw')}
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>
      {consentToWithdraw === null ? null : (
        <WithdrawConsentDialog
          consent={consentToWithdraw}
          isSubmitting={withdrawMutation.isPending}
          onConfirm={function confirm() {
            withdraw(consentToWithdraw);
          }}
          onCancel={function cancel() {
            setConsentToWithdraw(null);
          }}
        />
      )}
    </Card>
  );
}
